import { useEffect, useRef, useState } from "react";
import { api } from "../net/api.js";
import { TURNSTILE_ACTION, TURNSTILE_SITE_KEY } from "../net/turnstile.js";

interface TurnstileOptions {
  sitekey: string;
  action: string;
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
}

interface TurnstileApi {
  render(element: HTMLElement, options: TurnstileOptions): string | number;
  reset(widgetId?: string | number): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("turnstile_unavailable"));
  if (window.turnstile !== undefined) return Promise.resolve();
  if (scriptPromise !== null) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById("cloudflare-turnstile-script");
    const script = existing instanceof HTMLScriptElement ? existing : document.createElement("script");
    script.id = "cloudflare-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("turnstile_script_failed")), { once: true });
    if (existing === null) document.head.appendChild(script);
  });
  return scriptPromise;
}

export function useTurnstilePolicy(): boolean | null {
  const [required, setRequired] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    void api.turnstilePolicy()
      .then((policy) => { if (active) setRequired(policy.required); })
      .catch(() => { if (active) setRequired(true); });
    return () => { active = false; };
  }, []);
  return required;
}

export function TurnstileWidget({ enabled, resetKey, onToken }: {
  enabled: boolean;
  resetKey: number;
  onToken: (token: string | null) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | number | null>(null);
  const onTokenRef = useRef(onToken);
  useEffect(() => { onTokenRef.current = onToken; }, [onToken]);

  useEffect(() => {
    if (!enabled) {
      onTokenRef.current(null);
      return;
    }
    let active = true;
    void loadTurnstile().then(() => {
      if (!active || container.current === null || window.turnstile === undefined) return;
      container.current.replaceChildren();
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: TURNSTILE_ACTION,
        callback: (token) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(null),
        "error-callback": () => onTokenRef.current(null),
      });
    }).catch(() => onTokenRef.current(null));

    return () => {
      active = false;
      if (widgetId.current !== null && window.turnstile !== undefined) window.turnstile.reset(widgetId.current);
      widgetId.current = null;
      if (container.current !== null) container.current.replaceChildren();
      onTokenRef.current(null);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    onTokenRef.current(null);
    if (widgetId.current !== null && window.turnstile !== undefined) window.turnstile.reset(widgetId.current);
  }, [enabled, resetKey]);

  if (!enabled) return null;
  return <div ref={container} className="turnstile-widget" data-sitekey={TURNSTILE_SITE_KEY} data-action={TURNSTILE_ACTION} />;
}
